class CarbonTabs extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  tabs = [];

  /** @type {HTMLElement[]} */
  panels = [];

  /** @type {ResizeObserver | null} */
  resizeObserver = null;

  /** @type {number | null} */
  resizeFrame = null;

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'), (tab) => /** @type {HTMLButtonElement} */ (tab));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'), (panel) => /** @type {HTMLElement} */ (panel));
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.select(tab)));
    this.addEventListener('keydown', (event) => this.onKeydown(/** @type {KeyboardEvent} */ (event)));
    this.panels.forEach((panel) => {
      const previous = /** @type {HTMLButtonElement | null} */ (panel.querySelector('[data-carbon-pagination-previous]'));
      const next = /** @type {HTMLButtonElement | null} */ (panel.querySelector('[data-carbon-pagination-next]'));
      previous?.addEventListener('click', () => this.changePage(panel, -1));
      next?.addEventListener('click', () => this.changePage(panel, 1));
    });

    this.resizeObserver = new ResizeObserver(() => this.schedulePaginationUpdate());
    this.resizeObserver.observe(this);
    requestAnimationFrame(() => this.updatePagination());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
  }

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} [focus]
   */
  select(tab, focus = false) {
    const target = tab.getAttribute('aria-controls');
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== target;
      if (!panel.hidden) panel.dataset.carbonPage = '1';
    });
    if (focus) tab.focus();
    requestAnimationFrame(() => this.updatePagination());
  }

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const current = this.tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (current < 0) return;
    event.preventDefault();
    let next = current;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;
    this.select(this.tabs[next], true);
  }

  schedulePaginationUpdate() {
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.updatePagination();
    });
  }

  updatePagination() {
    this.panels.forEach((panel) => this.updatePanelPagination(panel));
  }

  /** @param {HTMLElement} panel */
  updatePanelPagination(panel) {
    const items = Array.from(panel.querySelectorAll('[data-carbon-product]'), (item) => /** @type {HTMLElement} */ (item));
    const pagination = /** @type {HTMLElement | null} */ (panel.querySelector('[data-carbon-pagination]'));
    if (!pagination) return;

    const productsPerPage = this.getProductsPerPage(panel);
    const totalPages = Math.max(1, Math.ceil(items.length / productsPerPage));
    const currentPage = Math.min(Math.max(Number(panel.dataset.carbonPage) || 1, 1), totalPages);
    panel.dataset.carbonPage = String(currentPage);

    items.forEach((item, index) => {
      const start = (currentPage - 1) * productsPerPage;
      item.hidden = index < start || index >= start + productsPerPage;
    });

    pagination.hidden = totalPages <= 1;
    const previous = /** @type {HTMLButtonElement | null} */ (pagination.querySelector('[data-carbon-pagination-previous]'));
    const next = /** @type {HTMLButtonElement | null} */ (pagination.querySelector('[data-carbon-pagination-next]'));
    const current = /** @type {HTMLElement | null} */ (pagination.querySelector('[data-carbon-pagination-current]'));
    const total = /** @type {HTMLElement | null} */ (pagination.querySelector('[data-carbon-pagination-total]'));
    if (previous) previous.disabled = currentPage <= 1;
    if (next) next.disabled = currentPage >= totalPages;
    if (current) current.textContent = String(currentPage);
    if (total) total.textContent = String(totalPages);
  }

  /** @param {HTMLElement} panel */
  getProductsPerPage(panel) {
    const grid = /** @type {HTMLElement | null} */ (panel.querySelector('[data-carbon-product-grid]'));
    if (!grid) return 1;

    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const columns = Number(desktop ? grid.dataset.carbonDesktopColumns : grid.dataset.carbonMobileColumns) || 1;
    const rows = Number(desktop ? grid.dataset.carbonDesktopRows : grid.dataset.carbonMobileRows) || 1;
    return Math.max(1, columns * rows);
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} direction
   */
  changePage(panel, direction) {
    const productsPerPage = this.getProductsPerPage(panel);
    const items = panel.querySelectorAll('[data-carbon-product]');
    const totalPages = Math.max(1, Math.ceil(items.length / productsPerPage));
    const currentPage = Number(panel.dataset.carbonPage) || 1;
    panel.dataset.carbonPage = String(Math.min(Math.max(currentPage + direction, 1), totalPages));
    this.updatePanelPagination(panel);
  }
}

if (!customElements.get('carbon-tabs')) customElements.define('carbon-tabs', CarbonTabs);
